/**
 * A conferência de integridade na Custódia (E134.F2, GUS-159, ADR-0090): a última conferência do
 * caso, com o que conferiu e o que divergiu, e a ação "Conferir integridade", que lê cada arquivo
 * guardado e refaz o SHA-256 do recebimento. A conferência fica registrada no servidor; a tela
 * relê a abertura para a árvore mostrar a situação nova de cada arquivo.
 *
 * O arquivo que diverge ou sumiu vira aviso: o uso dele está interrompido no servidor, e o
 * histórico das conferências fica.
 */
import { useState } from 'react';
import type { AxiosInstance } from 'axios';

import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { ResumoDaConferenciaData } from '@/lib/abertura_do_caso';
import { ConferenciaDaCustodiaError, conferirCustodia } from '@/lib/custodia';
import { formatarDataHora } from '@/lib/trilha_format';

import { AvisoDaAbertura, CartaoDaAbertura, ParDeDado } from './PecasDaAbertura';

const t = ptBR.abertura.custodia;

function partesDoResultado(ultima: ResumoDaConferenciaData): string[] {
  const partes = [
    t.conferenciaResultado
      .replace('{conferem}', String(ultima.conferem))
      .replace('{total}', String(ultima.total)),
  ];
  if (ultima.divergem > 0)
    partes.push(t.conferenciaDivergem.replace('{n}', String(ultima.divergem)));
  if (ultima.ausentes > 0)
    partes.push(t.conferenciaAusentes.replace('{n}', String(ultima.ausentes)));
  if (ultima.sem_registro > 0) {
    partes.push(t.conferenciaSemRegistro.replace('{n}', String(ultima.sem_registro)));
  }
  return partes;
}

function UltimaConferencia({ ultima }: { ultima: ResumoDaConferenciaData }): JSX.Element {
  const quando = formatarDataHora(ultima.conferido_em);
  return (
    <>
      <ParDeDado rotulo={t.conferenciaUltima}>
        <span data-testid="custodia-conferencia-quando">
          {ultima.origem === 'leitura' ? `${quando} · ${t.conferenciaNaLeitura}` : quando}
        </span>
      </ParDeDado>
      <p className="text-body-sm text-fg" data-testid="custodia-conferencia-resultado">
        {partesDoResultado(ultima).join(' · ')}
      </p>
    </>
  );
}

export interface ConferenciaDaCustodiaProps {
  client: AxiosInstance;
  caseId: string;
  ultima: ResumoDaConferenciaData | null | undefined;
  onConferido: () => Promise<void>;
  /** E143.F2: na abertura pelo banco a reconferência é da equipe (sem a ação). */
  somenteLeitura?: boolean;
}

export function ConferenciaDaCustodia({
  client,
  caseId,
  ultima,
  onConferido,
  somenteLeitura = false,
}: ConferenciaDaCustodiaProps): JSX.Element {
  const [conferindo, setConferindo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function conferir(): Promise<void> {
    setConferindo(true);
    setErro(null);
    try {
      await conferirCustodia(client, caseId);
      await onConferido();
    } catch (err) {
      const semPermissao =
        err instanceof ConferenciaDaCustodiaError && err.motivo === 'sem_permissao';
      setErro(semPermissao ? t.conferirSemPermissao : t.conferirErro);
    } finally {
      setConferindo(false);
    }
  }

  const divergiu = ultima !== null && ultima !== undefined && ultima.divergem + ultima.ausentes > 0;
  return (
    <CartaoDaAbertura titulo={t.conferenciaTitulo} testId="custodia-conferencia">
      {divergiu ? (
        <AvisoDaAbertura
          tom="aviso"
          titulo={t.conferenciaAlertaTitulo}
          texto={t.conferenciaAlertaTexto}
          testId="custodia-conferencia-alerta"
        />
      ) : null}
      {ultima ? (
        <UltimaConferencia ultima={ultima} />
      ) : (
        <p className="text-body-sm text-fg-muted" data-testid="custodia-conferencia-nunca">
          {t.conferenciaNunca}
        </p>
      )}
      <p className="text-caption text-fg-muted">{t.conferenciaExplica}</p>
      {somenteLeitura ? null : (
        <div className="flex flex-wrap items-center gap-sc-3">
          <Button
            type="button"
            variant="secondary"
            data-testid="custodia-conferir"
            disabled={conferindo}
            onClick={() => void conferir()}
          >
            {conferindo ? t.conferindo : t.conferir}
          </Button>
          {erro !== null ? (
            <p
              role="alert"
              className="text-body-sm text-danger"
              data-testid="custodia-conferir-erro"
            >
              {erro}
            </p>
          ) : null}
        </div>
      )}
    </CartaoDaAbertura>
  );
}
